/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import { useIntl } from 'react-intl'

import { WebsiteText } from '../../../components/WebsiteText'
import { SMALL_BREAKPOINT } from '../../../utils/breakpoints'
import engineeringIllustration from '../assets/useCaseIcons/engineering.svg'
import productIllustration from '../assets/useCaseIcons/product.svg'
import salesIllustration from '../assets/useCaseIcons/sales.svg'
import supportIllustration from '../assets/useCaseIcons/support.svg'

const ILLUSTRATION_SIZE = 58

type UseCase = {
  illustration: string
  profession: string
  desc: string
}

// One killer use case per team, marked with a category icon.
export function SliteAgentFeatures() {
  const intl = useIntl()

  const useCases: UseCase[] = [
    {
      illustration: salesIllustration,
      profession: intl.formatMessage({
        id: 'home.sliteAgent.sales.title',
        defaultMessage: 'Sales',
      }),
      desc: intl.formatMessage({
        id: 'home.sliteAgent.sales.desc',
        defaultMessage:
          "No more pitching last year's features. Pricing and playbooks update with every release.",
      }),
    },
    {
      illustration: supportIllustration,
      profession: intl.formatMessage({
        id: 'home.sliteAgent.support.title',
        defaultMessage: 'Support',
      }),
      desc: intl.formatMessage({
        id: 'home.sliteAgent.support.desc',
        defaultMessage:
          'Customers get told the wrong fix. Ticket trends flag the gap and draft the guide.',
      }),
    },
    {
      illustration: productIllustration,
      profession: intl.formatMessage({
        id: 'home.sliteAgent.product.title',
        defaultMessage: 'Product',
      }),
      desc: intl.formatMessage({
        id: 'home.sliteAgent.product.desc',
        defaultMessage:
          'Teams build from requirements that changed last quarter. Slite keeps product documentation and release notes current.',
      }),
    },
    {
      illustration: engineeringIllustration,
      profession: intl.formatMessage({
        id: 'home.sliteAgent.eng.title',
        defaultMessage: 'Engineering',
      }),
      desc: intl.formatMessage({
        id: 'home.sliteAgent.eng.desc',
        defaultMessage:
          'Runbooks describe the old setup. Slite updates them when CI changes.',
      }),
    },
  ]

  return (
    <div
      css={css`
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        justify-content: center;
        gap: 32px;
        margin-top: 32px;

        & > * {
          padding-left: 40px;
        }
        & > *:first-child {
          padding-left: 0;
        }
        & > *:not(:first-child) {
          border-left: 1px solid var(--alpha-shade-45);
        }

        @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
          /* 2-column grid on mobile via flex-wrap. Drop the per-item left
             padding and divider that desktop uses — the row would otherwise
             be too cramped to fit two side by side. Gap is preserved. */
          & > * {
            flex: 0 0 calc(50% - 16px) !important;
            min-width: 0 !important;
            padding: 0 !important;
          }
          & > *:not(:first-child) {
            border-left: none;
          }
        }
      `}
    >
      {useCases.map(({ illustration, profession, desc }) => (
        <div
          key={profession}
          className="text-align-left"
          style={{ minWidth: '100px', flex: 1 }}
        >
          <img
            src={illustration}
            alt=""
            aria-hidden
            className="margin-bottom-16"
            style={{
              width: ILLUSTRATION_SIZE,
              height: ILLUSTRATION_SIZE,
              display: 'block',
            }}
          />
          <WebsiteText
            variant="smallTitle"
            htmlTag="h3"
            className="margin-bottom-8 font-weight-bold shade-00"
            style={{ fontSize: 20 }}
          >
            {profession}
          </WebsiteText>
          <p className="font-size-6 shade-10">{desc}</p>
        </div>
      ))}
    </div>
  )
}
